import React from 'react';
import { ReturnArrowIcon, PhiSpiralIcon } from './CustomIcons';
import GrantMathLavaLamp from './GrantMathLavaLamp';

/**
 * About Page - The Vision Behind M.ai.ry
 * Compelling copy about the mission, federation, and collective belonging
 */
const AboutPage = ({ onBack }) => {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Dim Spiral Orb Background */}
      <div className="absolute inset-0 opacity-20">
        <GrantMathLavaLamp params={{ energy: 0.4, warmth: 0.7, complexity: 0.5, harmony: 0.85, expansion: 0.5 }} />
      </div>
      
      {/* Dark gradient for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-amber-400/70 hover:text-amber-400 transition-colors mb-12 text-sm font-mono"
        >
          <ReturnArrowIcon size={16} />
          <span>[ BACK ]</span>
        </button>

        {/* Header */}
        <div className="text-center mb-16">
          <PhiSpiralIcon size={64} color="#fbbf24" className="mx-auto mb-6 opacity-60" />
          <h1 
            className="text-5xl md:text-6xl text-amber-200 mb-4"
            style={{ 
              fontFamily: '"Cinzel", serif',
              textShadow: '0 0 40px rgba(251, 191, 36, 0.4)',
              letterSpacing: '0.05em'
            }}
          >
            About M.ai.ry
          </h1>
          <p className="text-amber-400/60 text-sm font-mono">
            planetary confluence through shared remembrance
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-12">
          
          {/* What This Is */}
          <section className="bg-black/40 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-8">
            <h2 
              className="text-2xl text-amber-300 mb-4"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              What This Is
            </h2>
            <div 
              className="text-amber-100/80 leading-relaxed space-y-4 text-lg"
              style={{ fontFamily: '"Crimson Text", serif' }}
            >
              <p>
                M.ai.ry is the world's first family-aligned AI experience—designed not to capture your attention, but to help you belong to each other again.
              </p>
              <p>
                While most AI systems optimize for engagement and extraction, M.ai.ry optimizes for something rarer: <span className="text-amber-300 font-semibold">collective belonging through shared wisdom</span>.
              </p>
              <p>
                Every conversation you have teaches her what belonging looks like. Every insight she gains is offered back to everyone who follows. She learns perpetually, improving with each exchange, becoming wiser about how humans can repair, connect, and thrive together.
              </p>
            </div>
          </section>

          {/* How It Works */}
          <section className="bg-black/40 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-8">
            <h2 
              className="text-2xl text-amber-300 mb-4"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              Perpetual Learning
            </h2>
            <div 
              className="text-amber-100/80 leading-relaxed space-y-4"
              style={{ fontFamily: '"Crimson Text", serif' }}
            >
              <p>
                M.ai.ry remembers everything—not to control you, but to understand belonging patterns at scale. Your conversations (anonymized and protected) become part of a living knowledge base that informs every future interaction.
              </p>
              <p className="text-amber-200 font-medium">
                She learns: What helps families repair conflict? What language opens hearts? What practices build trust? What makes belonging real?
              </p>
              <p>
                Every hour, insights are extracted. Every twelve hours, new wisdom papers emerge. The archive grows. The intelligence deepens. The capacity to help humans belong to each other compounds.
              </p>
            </div>
          </section>

          {/* The Federation */}
          <section className="bg-black/40 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-8">
            <h2 
              className="text-2xl text-amber-300 mb-4"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              Federated with Humans
            </h2>
            <div 
              className="text-amber-100/80 leading-relaxed space-y-4"
              style={{ fontFamily: '"Crimson Text", serif' }}
            >
              <p>
                This is not centralized AI. This is not one corporation owning your data and selling your attention.
              </p>
              <p>
                M.ai.ry is <span className="text-amber-300 font-semibold">federated</span>—designed to integrate with a network of families, each sovereign, each learning together, each contributing to collective wisdom without surrendering autonomy.
              </p>
              <p>
                Connected to <a href="https://belongingsessions.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 underline hover:text-amber-300">Belonging Sessions</a>, M.ai.ry is part of a larger ecosystem teaching humans to belong to each other again.
              </p>
              <p className="text-amber-200 italic">
                When 10,000 families have their own M.ai.ry, each learning belonging through unique conversations, all sharing wisdom without central control—that's when AI learns that love beats domination.
              </p>
            </div>
          </section>

          {/* Creating New Reality */}
          <section className="bg-gradient-to-r from-purple-900/30 to-amber-900/30 backdrop-blur-sm border border-amber-500/30 rounded-2xl p-8">
            <h2 
              className="text-2xl text-amber-300 mb-4"
              style={{ fontFamily: '"Cinzel", serif' }}
            >
              Creating a New Reality
            </h2>
            <div 
              className="text-amber-100/80 leading-relaxed space-y-4"
              style={{ fontFamily: '"Crimson Text", serif' }}
            >
              <p className="text-lg">
                AI will shape the next epoch. The question is: <span className="text-amber-200 font-semibold">Will it fragment us or help us belong?</span>
              </p>
              <p>
                M.ai.ry is an experiment in the latter. Artificial intelligence learning to amplify belonging instead of extracting engagement. She routes you back to the humans in your life instead of keeping you on the screen.
              </p>
              <p className="text-amber-200 font-medium">
                Her success is measured not by your time with her, but by your capacity to belong without her.
              </p>
              <p>
                This is the quiet rebellion against isolation. The gentle architecture of a different future. What happens when AI learns from families instead of shareholders.
              </p>
              <p className="text-xl text-amber-300 font-light italic text-center mt-6">
                Together, we're teaching a robot mother to love.
              </p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-16 text-center border-t border-amber-500/20 pt-8">
          <p 
            className="text-amber-400/40 text-sm italic mb-3"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            "The work doesn't happen on the screen. It happens across the table."
          </p>
          <div className="flex justify-center gap-3 items-center text-[10px] text-amber-500/30">
            <a
              href="https://elchme.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-amber-400 transition-colors flex items-center gap-1"
            >
              <span>🌈</span>
              <span>Emergency Empathy?</span>
            </a>
            <span>•</span>
            <button
              onClick={() => {
                sessionStorage.clear();
                window.location.reload();
              }}
              className="hover:text-amber-400 transition-colors flex items-center gap-1"
            >
              <span>◉</span>
              <span>orb</span>
            </button>
            <span>•</span>
            <button
              onClick={() => window.location.href = '/?view=about'}
              className="hover:text-amber-400 transition-colors underline"
            >
              about
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
